import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { generateClient } from 'aws-amplify/api';
import type { Schema } from '../../amplify/data/resource';
import PageHeader from '@/components/layout/PageHeader';
import WineStyleBar from '@/components/ui/WineStyleBar';
import FoodPairingChips from '@/components/recommendation/FoodPairingChips';
import AddToCellarModal from '@/components/cellar/AddToCellarModal';
import StarRating from '@/components/ui/StarRating';
import Button from '@/components/ui/Button';
import { useAddToCellar } from '@/hooks/useCellar';
import type { WineSummary } from '@/api/wines';

const client = generateClient<Schema>();

const STYLE_LABEL: Record<string, string> = {
  RED: 'Red', WHITE: 'White', ROSE: 'Rosé',
  SPARKLING: 'Sparkling', DESSERT: 'Dessert', FORTIFIED: 'Fortified',
};

export default function WineDetail() {
  const { wineId } = useParams<{ wineId: string }>();
  const navigate = useNavigate();
  const [cellarOpen, setCellarOpen] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const addToCellar = useAddToCellar();
  const queryClient = useQueryClient();

  const { data: wine, isLoading } = useQuery({
    queryKey: ['wine', wineId],
    queryFn: async () => {
      const { data } = await client.models.Wine.get({ id: wineId! });
      return data;
    },
    enabled: !!wineId,
  });

  const { data: tastings = [] } = useQuery({
    queryKey: ['wine-tastings', wineId],
    queryFn: async () => {
      const { data } = await client.models.UserWine.list({
        filter: { wineId: { eq: wineId! } },
      });
      return (data ?? []).sort(
        (a, b) => new Date(b.tastedAt).getTime() - new Date(a.tastedAt).getTime()
      );
    },
    enabled: !!wineId,
  });

  if (isLoading) return <div className="page"><p className="loading-text">Loading…</p></div>;
  if (!wine) return <div className="page"><p>Wine not found.</p></div>;

  const wineSummary: WineSummary = {
    id: wine.id,
    name: wine.name,
    producer: wine.producer ?? undefined,
    vintage: wine.vintage ?? undefined,
    style: wine.style ?? undefined,
    region: wine.region ?? undefined,
  };

  const hasStyleBars = wine.body || wine.tannin || wine.acidity || wine.oak || wine.sweetness;
  const hasWineInfo = wine.servingTemp || wine.agingPotential || wine.priceRange;
  const isEnriched = !!(wine.aiTastingNotes || wine.description || wine.enrichedAt);

  const ratedTastings = tastings.filter((t) => t.rating && t.rating > 0);
  const avgRating = ratedTastings.length
    ? ratedTastings.reduce((sum, t) => sum + (t.rating ?? 0), 0) / ratedTastings.length
    : null;

  async function handleEnrich() {
    setEnriching(true);
    try {
      await client.mutations.enrichWine({ wineId: wine!.id });
      await queryClient.invalidateQueries({ queryKey: ['wine', wineId] });
    } finally {
      setEnriching(false);
    }
  }

  return (
    <div className="page page-wine-detail">
      <PageHeader title="" back />

      {/* Hero */}
      <div className="wine-detail-hero">
        <h1 className="wine-detail-name">{wine.name}</h1>
        <p className="wine-detail-meta">
          {[wine.producer, wine.vintage, wine.region, wine.country].filter(Boolean).join(' · ')}
        </p>
        <div className="wine-detail-tags">
          {wine.style && (
            <span className="wine-tag">{STYLE_LABEL[wine.style] ?? wine.style}</span>
          )}
          {wine.grapes && wine.grapes.length > 0 && (
            <span className="wine-tag">{wine.grapes.join(', ')}</span>
          )}
          {wine.priceRange && (
            <span className="wine-tag wine-tag-price">{wine.priceRange}</span>
          )}
        </div>
        {wine.vivinoRating && (
          <p className="wine-detail-vivino">Vivino {wine.vivinoRating.toFixed(1)}</p>
        )}
        {avgRating !== null && (
          <div className="wine-detail-avg-rating">
            <StarRating value={Math.round(avgRating)} readonly size="sm" />
            <span className="wine-detail-avg-label">
              {avgRating.toFixed(1)} avg · {ratedTastings.length} tasting{ratedTastings.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {/* Enrich nudge for wines without AI content */}
      {!isEnriched && (
        <div className="wine-detail-enrich-banner">
          <p>No AI notes yet for this wine.</p>
          <Button variant="secondary" onClick={handleEnrich} loading={enriching}>
            Generate AI notes
          </Button>
        </div>
      )}

      {/* About */}
      {wine.description && (
        <section className="wine-detail-section">
          <h2 className="wine-detail-section-title">About</h2>
          <p className="wine-detail-description">{wine.description}</p>
        </section>
      )}

      {/* AI Tasting Notes */}
      {wine.aiTastingNotes && (
        <section className="wine-detail-section">
          <h2 className="wine-detail-section-title">AI Tasting Notes</h2>
          <p className="wine-detail-description">{wine.aiTastingNotes}</p>
        </section>
      )}

      {/* Style bars */}
      {hasStyleBars && (
        <section className="wine-detail-section">
          <h2 className="wine-detail-section-title">Style</h2>
          {wine.body     && <WineStyleBar label="Body"      value={wine.body}      lowLabel="Light"   highLabel="Full"    />}
          {wine.tannin   && <WineStyleBar label="Tannin"    value={wine.tannin}    lowLabel="Soft"    highLabel="Grippy"  />}
          {wine.acidity  && <WineStyleBar label="Acidity"   value={wine.acidity}   lowLabel="Low"     highLabel="Crisp"   />}
          {wine.oak      && <WineStyleBar label="Oak"       value={wine.oak}       lowLabel="Unoaked" highLabel="Oaked"   />}
          {wine.sweetness && <WineStyleBar label="Sweetness" value={wine.sweetness} lowLabel="Dry"     highLabel="Sweet"   />}
        </section>
      )}

      {/* Wine info */}
      {hasWineInfo && (
        <section className="wine-detail-section">
          <h2 className="wine-detail-section-title">Wine info</h2>
          <dl className="wine-detail-info-list">
            {wine.servingTemp && (
              <>
                <dt>Serving temp</dt>
                <dd>{wine.servingTemp}</dd>
              </>
            )}
            {wine.agingPotential && (
              <>
                <dt>Drinking window</dt>
                <dd>{wine.agingPotential}</dd>
              </>
            )}
            {wine.priceRange && (
              <>
                <dt>Price range</dt>
                <dd style={{ textTransform: 'capitalize' }}>{wine.priceRange}</dd>
              </>
            )}
          </dl>
        </section>
      )}

      {/* Food pairings */}
      {wine.foodPairings && wine.foodPairings.length > 0 && (
        <section className="wine-detail-section">
          <h2 className="wine-detail-section-title">Food pairings</h2>
          <FoodPairingChips
            pairings={wine.foodPairings.filter(Boolean) as string[]}
            onSelect={(food) => navigate(`/recommend?food=${encodeURIComponent(food)}`)}
          />
        </section>
      )}

      {/* My tastings */}
      {tastings.length > 0 && (
        <section className="wine-detail-section">
          <h2 className="wine-detail-section-title">My tastings</h2>
          <ul className="wine-detail-tastings">
            {tastings.map((t) => (
              <li key={t.id} className="wine-detail-tasting-item">
                <div className="wine-detail-tasting-header">
                  {t.rating && <StarRating value={t.rating} readonly size="sm" />}
                  <span className="wine-detail-tasting-date">
                    {new Date(t.tastedAt).toLocaleDateString('en-GB', {
                      day: 'numeric', month: 'short', year: 'numeric',
                    })}
                  </span>
                </div>
                {t.notes && <p className="wine-detail-tasting-notes">{t.notes}</p>}
                {t.occasion && <p className="wine-detail-tasting-occasion">{t.occasion}</p>}
                {t.location && <p className="wine-detail-tasting-occasion">{t.location}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="wine-detail-actions">
        <Button variant="primary" fullWidth onClick={() => navigate(`/add?wineId=${wine.id}`)}>
          Log a tasting
        </Button>
        <Button variant="secondary" fullWidth onClick={() => setCellarOpen(true)}>
          Add to cellar
        </Button>
      </div>

      <AddToCellarModal
        wine={wineSummary}
        open={cellarOpen}
        onClose={() => setCellarOpen(false)}
        onAdd={addToCellar}
      />
    </div>
  );
}
